import React, { Fragment } from 'react'
import styled, { css } from 'styled-components/macro'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import theme from '../../../theme'
import { Graphic, Text, Spacing } from '../../ui'
import { omit } from 'ramda'

const DEFAULT_COLOR = 'main'

const getBackgroundColor = props => {
  if (props.outline) return theme.color.white
  if (props.transparent) return 'transparent'

  return theme.color[props.themeColor || DEFAULT_COLOR]
}

const getTextThemeColor = props => {
  if (props.outline || props.transparent)
    return props.themeColor || DEFAULT_COLOR

  return 'white'
}

const getBorderColor = (props, { hover = false } = {}) => {
  const currentColor = theme.color[props.themeColor || DEFAULT_COLOR]

  if (props.outline && !hover) return currentColor + 'AA'
  if (props.transparent) return 'transparent'

  return currentColor
}

const getGraphicColor = props => {
  if (props.graphicColor) return props.graphicColor

  return getTextThemeColor(props)
}

const renderIcon = props => (
  <Graphic
    name={props.graphic}
    size={props.graphicSize || 16}
    themeColor={getGraphicColor(props)}
  />
)

const getPadding = ({ link, size }) => {
  if (link) {
    return 0
  }

  switch (size) {
    case 'small':
      return `${theme.padding.s} ${theme.padding.m}`
    case 'normal':
    default:
      return `${theme.padding.m} ${theme.padding.xl}`
  }
}

const ButtonChildren = props => {
  const color = getTextThemeColor(props)

  return (
    <MaybeLink {...props}>
      <button
        onClick={props.onClick}
        type="button"
        {...omit(
          [
            'block',
            'link',
            'transparent',
            'primary',
            'outline',
            'rounded',
            'size'
          ],
          props
        )}
      >
        <Spacing direction="row" justify={props.justify} align="center">
          {props.graphic && renderIcon(props)}
          {props.prefix && (
            <Text h6 color={color} inline>
              {props.prefix}
            </Text>
          )}

          <Text h6 color={color} inline center>
            {props.children}
          </Text>
        </Spacing>
      </button>
    </MaybeLink>
  )
}

const MaybeLink = ({ to, children, ...props }) =>
  to ? <Link to={to}>{children}</Link> : <Fragment>{children}</Fragment>

export const Button = styled(ButtonChildren)`
  ${props => {
    const { theme, outline, block, rounded } = props

    const backgroundColor = getBackgroundColor(props)
    const borderColor = getBorderColor(props)
    const borderColorHover = getBorderColor(props, { hover: true })

    return css`
      background: ${backgroundColor};

      border: 1px solid ${borderColor};
      border-radius: ${theme.borderRadius.base};
      justify-content: center;

      padding: ${getPadding(props)};

      cursor: pointer;
      outline: none;

      transition: opacity ${theme.animationSpeed.slow} ease-in-out;

      ${block && `width: 100%;`}
      ${outline && `box-shadow: ${theme.boxShadow.default};`}
      ${rounded && `border-radius: 50px;`}

      &:hover {
        opacity: ${!outline ? 0.7 : 1};
        border: 1px solid ${borderColorHover};
        transition: border-color ${theme.animationSpeed.normal};
      }

      &:disabled {
        cursor: not-allowed;
        border: 1px solid ${borderColor};
        pointer-events: none;
        opacity: 0.4;
      }
    `
  }}
`
const propOptions = {
  colors: Object.keys(theme.color)
}

Button.defaultProps = {
  justify: 'center'
}

Button.propTypes = {
  disabled: PropTypes.bool,
  outline: PropTypes.bool,
  link: PropTypes.bool,
  transparent: PropTypes.bool,
  themeColor: PropTypes.oneOf(propOptions.colors),
  graphic: PropTypes.string,
  graphicColor: PropTypes.oneOf(propOptions.colors),
  prefix: PropTypes.string,
  justify: PropTypes.string,
  bold: PropTypes.bool,
  size: PropTypes.string,
  rounded: PropTypes.bool
}
